import React, { useEffect, useMemo, useState } from "react";
import {
    AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
    Box,
    Button,
    Card,
    Flex,
    FormControl,
    FormLabel,
    IconButton,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Stack,
    Table, Tbody, Td,
    Text,
    Th,
    Thead,
    Tr, useColorModeValue
} from "@chakra-ui/react";
import Pagination from "rc-pagination";
import { useNewUser } from "../hooks/useNewUser";
import { cloneDeep } from "lodash";

import {
    ColumnFiltersState,
    SortingState,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    ColumnOrderState
} from '@tanstack/react-table'
import { UserModelGet } from "models/user_model";
import { HamburgerIcon } from "@chakra-ui/icons";

type NewUserTableProps = Pick<useNewUser.type, "state" | "add" | "cancelRef" | "closeDelete" | "deleteUser" | "editUser">;

const columnHelper = createColumnHelper<UserModelGet>();

const NewUserTable: React.FC<NewUserTableProps> = ({
    state,
    add,
    cancelRef,
    closeDelete,
    deleteUser,
    editUser,
}) => {
    const textColor = useColorModeValue("secondaryGray.900", "white");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");


    const listBase = useMemo(() => state.list, [state.list]);

    const defaultHeader = (headerText: string) => (
        <Text
            justifyContent="space-between"
            align="center"
            fontSize={{ sm: "10px", lg: "12px" }}
            color="gray.400"
        >
            {headerText}
        </Text>
    );

    // Função auxiliar para criar a célula
    const defaultCell = (info: any) => (
        <Flex align="center">
            <Text color={textColor} fontSize="sm" fontWeight="700">
                {info.getValue()}
            </Text>
        </Flex>
    );

    const columns = useMemo(() => [
        columnHelper.accessor("id", {
            id: "id",
            header: () => defaultHeader("#ID"),
            cell: defaultCell,
        }),
        columnHelper.accessor("nome", {
            id: "nome",
            header: () => defaultHeader("#NOME"),
            cell: defaultCell,
        }),
        columnHelper.accessor("cpf", {
            id: "cpf",
            header: () => defaultHeader("#CPF"),
            cell: defaultCell,
        }),
        columnHelper.accessor("rg", {
            id: "rg",
            header: () => defaultHeader("#RG"),
            cell: defaultCell,
        }),
        columnHelper.accessor("ativo", {
            id: "ativo",
            header: () => defaultHeader("#ATIVO"),
            cell: (info) => (
                <Flex align="center">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                        {info.getValue() ? "Sim" : "Não"}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor("email", {
            id: "email",
            header: () => defaultHeader("#E-MAIL"),
            cell: defaultCell,
        }),
        columnHelper.display({
            id: "opcoes",
            header: () => defaultHeader("#OPÇÕES"),
            cell: (info) => (
                <Flex align="center">
                    <Button onClick={() => editUser(info.row.original)} colorScheme="green" variant="ghost">
                        Editar
                    </Button>
                    <Button onClick={() => deleteUser(info.row.original)} colorScheme="red" variant="ghost" ml="1">
                        Excluir
                    </Button>
                </Flex>
            )
        })
    ], [editUser, deleteUser, textColor, defaultCell, defaultHeader]);

    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });
    const [filterValue, setFilterValue] = useState('');
    const [columnVisibility, setColumnVisibility] = React.useState({
        id: false,
        cpf: false,
        rg: false,
    })
    const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>([])

    const table = useReactTable({
        data: listBase,
        columns,
        state: {
            sorting,
            pagination,
            globalFilter: filterValue,
            columnVisibility,
            columnOrder,
        },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        globalFilterFn: (row, columnId, filterValue) => {
            const nome = row.getValue<string>("nome")?.toLowerCase() ?? '';
            const email = row.getValue<string>("email")?.toLowerCase() ?? '';
            return nome.includes(filterValue.toLowerCase()) || email.includes(filterValue.toLowerCase());
        },
        debugTable: true,
    });

    // const countPerPage = 10;
    // const [value, setValue] = useState("");
    // const [currentPage, setCurrentPage] = useState(1);
    // const [collection, setCollection] = useState(
    //     cloneDeep(state.list.slice(0, countPerPage))
    // );
    // const updatePage = (p: number) => {
    //     setCurrentPage(p);
    //     const to = countPerPage * p;
    //     const from = to - countPerPage;
    //     setCollection(cloneDeep(state.list.slice(from, to)));
    // };

    // useEffect(() => {
    //     const updatePage = (p: number) => {
    //         setCurrentPage(p);
    //         const to = countPerPage * p;
    //         const from = to - countPerPage;
    //         setCollection(cloneDeep(state.list.slice(from, to)));
    //     };

    //     const searchData = (val: string) => {
    //         const query = val.toLowerCase();
    //         setCurrentPage(1);
    //         const data =
    //             state.list
    //                 .filter(item => item?.nome.toLowerCase().includes(query))
    //                 .slice(0, countPerPage)
    //         setCollection(data);
    //     }

    //     if (!value) {
    //         updatePage(1);
    //     } else {
    //         searchData(value);
    //     }
    // }, [state.list, value]);

    return (
        <Card flexDirection="column" overflowX={{ sm: "scroll", lg: "hidden" }}>
            <Flex
                px="25px"
                mb="16px"
                mt="16px"
                justifyContent="space-between"
                align="center"
            >
                <Text
                    color={textColor}
                    fontSize="22px"
                    fontWeight="700"
                    lineHeight="100%"
                >
                    Listagem de Produtos
                </Text>
                <Button onClick={add} colorScheme="brand" variant="outline">
                    Adicionar
                </Button>
            </Flex>
            <Box>

                <Stack p={4}>
                    <FormControl>
                        <FormLabel
                            display="flex"
                            ms="4px"
                            fontSize="sm"
                            fontWeight="bold"
                            color={textColor}
                            mb="8px"

                        >
                            Buscar
                        </FormLabel>

                        <Input
                            fontSize="sm"
                            ms={{ base: "0px", md: "0px" }}
                            type="text"
                            placeholder="Buscar"
                            fontWeight="500"
                            w={320}
                            size="md"
                            mr="5px"
                            onChange={e => setFilterValue(e.target.value)}
                            value={filterValue}
                        />

                        <Menu>
                            <MenuButton
                                as={IconButton}
                                aria-label='Options'
                                icon={<HamburgerIcon />}
                                variant='outline'
                            />
                            <MenuList>
                                {table.getAllLeafColumns().filter(column => column.id !== 'opcoes').map(column => {
                                    return (
                                        <MenuItem key={column.id}>
                                            <label>
                                                <input
                                                    {...{
                                                        type: 'checkbox',
                                                        checked: column.getIsVisible(),
                                                        onChange: column.getToggleVisibilityHandler(),
                                                    }}
                                                />{' '}
                                                {column.id}
                                            </label>
                                        </MenuItem>
                                    )
                                })}
                            </MenuList>
                        </Menu>
                    </FormControl>
                </Stack>

                <Table variant="striped" color="gray.500" mb="24px" mt="12px">
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <Th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            pe='10px'
                                            borderColor={borderColor}
                                            cursor='pointer'
                                            onClick={header.column.getToggleSortingHandler()}>
                                            <Flex
                                                justifyContent='space-between'
                                                align='center'
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color='gray.400'>
                                                {flexRender(header.column.columnDef.header, header.getContext())}{{
                                                    asc: '',
                                                    desc: '',
                                                }[header.column.getIsSorted() as string] ?? null}
                                            </Flex>
                                        </Th>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <Tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <Td
                                                key={cell.id}
                                                fontSize={{ sm: '14px' }}
                                                minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                borderColor='transparent'>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>

                <Pagination
                    pageSize={table.getTotalSize()}
                    onChange={(value) => {
                        console.log("PAGINATION: ", value);
                    }}
                    current={table.getState().pagination.pageIndex}
                    total={state.list.length}
                    style={{
                        float: 'right',
                        marginRight: 24,
                        marginBottom: 24,
                    }}
                />

                {/* <Table variant="striped" color="gray.500" mb="24px" mt="12px">
                    <Thead>
                        <Tr>

                            <Th pe="10px" borderColor={borderColor} cursor="pointer">
                                <Flex
                                    justifyContent="space-between"
                                    align="center"
                                    fontSize={{sm: "10px", lg: "12px"}}
                                    color="gray.400"
                                >
                                    #NOME
                                </Flex>
                            </Th>
                            <Th pe="10px" borderColor={borderColor} cursor="pointer">
                                <Flex
                                    justifyContent="space-between"
                                    align="center"
                                    fontSize={{sm: "10px", lg: "12px"}}
                                    color="gray.400"
                                >
                                    #E-MAIL
                                </Flex>
                            </Th>
                            <Th pe="10px" borderColor={borderColor} cursor="pointer">
                                <Flex
                                    justifyContent="space-between"
                                    align="center"
                                    fontSize={{sm: "10px", lg: "12px"}}
                                    color="gray.400"
                                >
                                    #ATIVO
                                </Flex>
                            </Th>
                            <Th pe="10px" borderColor={borderColor} cursor="pointer">
                                <Flex
                                    justifyContent="space-between"
                                    align="center"
                                    fontSize={{sm: "10px", lg: "12px"}}
                                    color="gray.400"
                                >
                                    #OPÇÕES
                                </Flex>
                            </Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {collection?.map((item, index) => {
                            return (
                                <Tr key={`${item.id}-${item.idEmpresa}-${index}`}>
                                    <Td
                                        fontSize={{sm: "14px"}}
                                        borderColor="transparent"
                                    >
                                        {item?.nome}
                                    </Td>
                                    <Td
                                        fontSize={{sm: "14px"}}
                                        borderColor="transparent"
                                    >
                                        {item.email}
                                    </Td>

                                    <Td
                                        fontSize={{sm: "14px"}}
                                        borderColor="transparent"
                                    >
                                        {item?.ativo ? "Sim" : "Não"}
                                    </Td>
                                    <Td
                                        fontSize={{sm: "14px"}}
                                        borderColor="transparent"
                                    >
                                        <Button onClick={() => editUser(item)} colorScheme="green" variant="ghost">
                                            Editar
                                        </Button>
                                        <Button onClick={() => deleteUser(item)} colorScheme="red" variant="ghost"
                                                ml="1">
                                            Excluir
                                        </Button>
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table> */}

                <AlertDialog
                    isOpen={state.showDelete}
                    leastDestructiveRef={cancelRef}
                    onClose={closeDelete}
                    isCentered
                    motionPreset='slideInBottom'
                >
                    <AlertDialogOverlay>
                        <AlertDialogContent>
                            <AlertDialogHeader fontSize="lg" fontWeight="bold">
                                Cuidado!
                            </AlertDialogHeader>

                            <AlertDialogBody>
                                Deseja realmente deletar esse registro?
                            </AlertDialogBody>

                            <AlertDialogFooter>
                                <Button ref={cancelRef} onClick={closeDelete} colorScheme={'gray'}>
                                    Cancelar
                                </Button>
                                <Button ref={cancelRef} onClick={() => {
                                }} colorScheme={'red'}>
                                    Excluir
                                </Button>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialogOverlay>
                </AlertDialog>
            </Box>
        </Card>
    );
}

export default NewUserTable;
